import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MenuItem from '@material-ui/core/MenuItem';

import ApiEntitySelectDialog from '../../../../components/ApiEntitySelectDialog/ApiEntitySelectDialog';
import CustomInput, { Props as CustomInputProps } from '../../../../components/CustomInput/CustomInput';
import PlasticAssessment from '../../../../types/PlasticAssessment';
import api from '../../../../utils/api';
import { dateToLocale } from '../../../../utils/date';

interface Props {
    baseInputProps: Omit<CustomInputProps, 'name'>;
    companyId: number | string;
}

const AssessmentSelect = (props: Props) => {
    const { baseInputProps, companyId } = props;
    const { data, handleAttributeChange } = baseInputProps;

    const { t } = useTranslation();

    const [assessmentData, setAssessmentData] = useState<PlasticAssessment | null>(null);

    const [isSelectModalOpen, setIsSelectModalOpen] = useState(false);

    const onAssessmentSelect = useCallback((selectedAssessmentId?: number) => {
        if (selectedAssessmentId && handleAttributeChange) {
            handleAttributeChange('assessmentId', selectedAssessmentId.toString());
        }
        setIsSelectModalOpen(false);
    }, [handleAttributeChange]);

    const assessmentId = data.assessmentId;

    useEffect(() => {
        if (assessmentId) {
            api.request('/companies/' + companyId + '/plastic-assessments/' + assessmentId).then((res) => {
                setAssessmentData(res);

                if (handleAttributeChange) {
                    handleAttributeChange('co2Saved', (!res.savedCo2 ? res.savedCo2 : (res.savedCo2 * -1)).toString() + ' kg');
                    handleAttributeChange('reducedPlastic', (!res.reducedPlastic ? res.reducedPlastic : (res.reducedPlastic * -1)).toString() + ' kg');
                }
            });
        }
        else {
            setAssessmentData(null);
        }
    }, [handleAttributeChange, assessmentId, companyId]);

    const map = useCallback((assessment: PlasticAssessment) => {
        if (assessment.state !== 'audit' && (!assessmentData || assessment.id !== assessmentData.id)) {
            return null;
        }

        return {
            id: assessment.id,
            name: dateToLocale(assessment.creationDate, false),
            description: t('dialogs.plastic_assessment_select.last_edit', { date: dateToLocale(assessment.modificationDate) })
        };
    }, [assessmentData, t]);

    const selectProps = {
        open: false,
        onOpen: () => setIsSelectModalOpen(true)
    };

    return (
        <>
            <CustomInput {...baseInputProps} name='assessmentId' select SelectProps={selectProps} >
                {assessmentData ? (
                    <MenuItem value={assessmentData.id} >
                        {dateToLocale(assessmentData.creationDate, false)}
                    </MenuItem>
                ) : (
                    <MenuItem />
                )}
            </CustomInput>

            <ApiEntitySelectDialog
                open={isSelectModalOpen}
                onClose={onAssessmentSelect}
                title={t('dialogs.plastic_assessment_select.title')}
                noEntityFoundMessage={t('dialogs.plastic_assessment_select.no_kpi_found')}
                url={'/companies/' + companyId + '/plastic-assessments'}
                map={map}
                disableSearch
                disablePagination
            />
        </>
    );
};

export default AssessmentSelect;
